import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

/**
 * The primary CTA — the paper-on-ink (ink-on-paper in light) glow button.
 * This exact recipe used to be copy-pasted across the consumer app; keep
 * call-site classes to layout concerns only (w-full, ml-auto, mt-*) — they
 * win over the base via tailwind-merge.
 *
 * Not for the small round pills (rounded-full/rounded-lg quick actions) —
 * those are a different, quieter role.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(
  function Button({ className, type, ...props }, ref) {
    return (
      <button
        ref={ref}
        type={type ?? "button"}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-xl bg-paper light:bg-ink px-4 py-2.5 text-sm font-semibold text-ink light:text-paper shadow-[0_4px_18px_-6px_rgba(245,241,232,0.35)] transition hover:bg-white light:hover:bg-zinc-800",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300 light:focus-visible:ring-zinc-700 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 light:focus-visible:ring-offset-paper",
          "disabled:cursor-not-allowed disabled:bg-zinc-800 light:disabled:bg-zinc-300 disabled:text-zinc-500 disabled:shadow-none",
          className,
        )}
        {...props}
      />
    );
  },
);
